import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { formatJobDayClockTime } from './jobScheduleBounds.util';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Fallback when API does not return {@link Job.timezone} (older backends / cached jobs). */
export const DEFAULT_JOB_TIMEZONE = 'Australia/Sydney';

export type JobTimezoneSource =
  | { timezone?: string | null }
  | string
  | null
  | undefined;

/**
 * Resolve the IANA timezone for a job site (e.g. `Australia/Sydney`).
 * Use when formatting or interpreting unix-ms instants in job-local time.
 */
export function getJobTimezone(job?: JobTimezoneSource): string {
  if (typeof job === 'string') {
    const trimmed = job.trim();
    if (trimmed) {
      return trimmed;
    }
    return DEFAULT_JOB_TIMEZONE;
  }
  const value = job?.timezone?.trim();
  if (value) {
    return value;
  }
  return DEFAULT_JOB_TIMEZONE;
}

/**
 * True when the value is a unix-ms instant (e.g. daySequence `clockInTime`).
 * Rejects schedule strings like `21:00` and compact `2100`.
 */
export function isUnixMsTimestamp(
  value: string | number | null | undefined,
): boolean {
  const ms = Number(value);
  return Number.isFinite(ms) && ms >= 1e11;
}

/**
 * Format a unix-ms instant in the job site timezone.
 *
 * Use for read-only labels when viewers may be in a different timezone than the job site.
 * Do not use for portal/mobile time pickers — those pair with
 * {@link formatUnixMsAsLocalHHmm} / {@link getTimestampFromTime} (local calendar parsing).
 *
 * Do not use for: `job.days.startTime` / `job.days.endTime` — use
 * {@link formatJobDayClockTime} instead.
 */
export function formatInJobTimezone(
  timestamp: string | number | null | undefined,
  job?: JobTimezoneSource,
  format = 'HH:mm',
): string {
  if (!isUnixMsTimestamp(timestamp)) {
    return '';
  }
  return dayjs(Number(timestamp)).tz(getJobTimezone(job)).format(format);
}

/**
 * `HH:mm` from a unix-ms instant in the browser's local timezone.
 *
 * Use in time pickers and forms that save via local calendar parsing
 * (e.g. {@link getTimestampFromTime} / `parseScheduleDateLocal`).
 */
export function formatUnixMsAsLocalHHmm(
  unixMs: string | number | null | undefined,
): string {
  if (!isUnixMsTimestamp(unixMs)) {
    return '';
  }
  const formatted = dayjs(Number(unixMs)).format('HH:mm');
  return formatted === 'Invalid Date' ? '' : formatted;
}

/**
 * Value for `HH:mm` time pickers when the source may be either:
 * - a logged/proposed unix-ms instant, or
 * - a scheduled shift time from `job.days` when nothing is logged yet.
 */
export function formatTimeForTimePicker(
  unixMs: string | null | undefined,
  scheduleClockTime: string | null | undefined,
  _job?: JobTimezoneSource,
  fallback = '09:00',
): string {
  if (unixMs && isUnixMsTimestamp(unixMs)) {
    return formatUnixMsAsLocalHHmm(unixMs) || fallback;
  }

  return formatJobDayClockTime(scheduleClockTime) || fallback;
}
